/**
 * Firebase-Konfiguration und Initialisierung
 *
 * Auth wie Navigator/Task: authDomain = aktuelle Custom-Domain,
 * damit signInWithRedirect die Session behält.
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  initializeAuth,
  getAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getFirestore } from 'firebase/firestore';

function getFirebaseConfig() {
  const requiredEnvVars = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };

  const missingVars = Object.entries(requiredEnvVars)
    .filter(
      ([, value]) =>
        !value || (typeof value === 'string' && value.trim() === '')
    )
    .map(([key]) => key);

  if (missingVars.length > 0) {
    throw new Error(
      `Firebase-Konfiguration fehlt: ${missingVars.join(', ')}. Bitte .env prüfen.`
    );
  }

  return {
    apiKey: requiredEnvVars.apiKey.trim(),
    authDomain: requiredEnvVars.authDomain.trim(),
    projectId: requiredEnvVars.projectId.trim(),
    storageBucket: requiredEnvVars.storageBucket.trim(),
    messagingSenderId: requiredEnvVars.messagingSenderId.trim(),
    appId: requiredEnvVars.appId.trim(),
  };
}

/** Auth-Domain = aktuelle Origin, damit Redirect/Popup same-site bleiben. */
function resolveAuthDomain(fallback) {
  if (typeof window === 'undefined') return fallback;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'curiculumner.firebaseapp.com';
  }
  if (host === 'mail.acceler-8.ch' || host === 'mail-xlr8.web.app') {
    return host;
  }
  return fallback;
}

let app;
let auth;
let functions;
let db;

try {
  const base = getFirebaseConfig();
  const config = {
    ...base,
    authDomain: resolveAuthDomain(base.authDomain),
  };

  if (getApps().length === 0) {
    app = initializeApp(config);
  } else {
    app = getApps()[0];
  }

  try {
    auth = initializeAuth(app, {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    auth = getAuth(app);
  }

  functions = getFunctions(app, 'europe-west6');
  db = getFirestore(app);

  if (process.env.NODE_ENV === 'development') {
    console.info('[Firebase] Project ID:', config.projectId);
    console.info('[Firebase] Auth domain:', config.authDomain);
  }
} catch (error) {
  console.error('Firebase Initialisierung fehlgeschlagen:', error);
  throw error;
}

export { app, auth, functions, db };
